import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, Loader2, Beaker } from 'lucide-react';
import { api, formatError, imgUrl, imgSrcSet } from '../lib/api';
import { useContent } from '../context/ContentContext';
import { toast } from 'sonner';

const LAB_IMG = 'https://images.unsplash.com/photo-1770320742319-6aa889b3130b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxsYWJvcmF0b3J5JTIwZ2xhc3N3YXJlJTIwbmVvbiUyMGRhcmslMjBzY2llbmNlfGVufDB8fHx8MTc4ODk3OTA5MHww&ixlib=rb-4.1.0&q=85';

export const COMMUNITY_DEFAULTS = {
  newsEyebrow: 'Journal mycologique',
  newsTitle1: 'Protocoles, génétiques rares',
  newsTitle2: 'et fenêtres de fructification.',
  newsText: 'Deux envois par mois. Zéro spam, uniquement du mycélium de qualité éditoriale.',
  contactEyebrow: 'Support laboratoire',
  contactTitle1: 'Une contamination suspecte ?',
  contactTitle2: 'Un devis volume ?',
  contactText: 'Nos mycologues répondent sous 24 h ouvrées.',
};

const Community = () => {
  const c = useContent('community', COMMUNITY_DEFAULTS);
  const [nlEmail, setNlEmail] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: 'Support technique', message: '' });
  const [cLoading, setCLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setNlLoading(true);
    try {
      await api.post('/newsletter', { email: nlEmail });
      toast.success('Inoculation réussie — vérifiez votre boîte mail');
      setNlEmail('');
    } catch (err) {
      toast.error(formatError(err, "Inscription impossible"));
    } finally {
      setNlLoading(false);
    }
  };

  const sendContact = async (e) => {
    e.preventDefault();
    setCLoading(true);
    try {
      await api.post('/contact', form);
      toast.success('Transmission reçue — réponse sous 24 h');
      setForm({ name: '', email: '', subject: 'Support technique', message: '' });
    } catch (err) {
      toast.error(formatError(err, "Envoi impossible"));
    } finally {
      setCLoading(false);
    }
  };

  const inputCls = 'clip-frame-sm w-full border border-white/10 bg-void/60 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-neon/60 focus:outline-none transition-colors';

  return (
    <section id="contact" data-testid="community-section" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="clip-frame relative overflow-hidden border border-neon/20 lg:col-span-5"
          id="labo"
        >
          <img src={imgUrl(LAB_IMG, 1024)} srcSet={imgSrcSet(LAB_IMG)} sizes="100vw" alt="Verrerie de laboratoire TerraFungi" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/60 to-transparent" />
          <div className="scanlines relative flex h-full min-h-[420px] flex-col justify-end p-8">
            <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyber">
              <Beaker size={13} /> {c.newsEyebrow}
            </p>
            <h3 className="font-display text-2xl font-bold leading-tight text-slate-100">
              {c.newsTitle1}<br />{c.newsTitle2}
            </h3>
            <p className="mt-3 max-w-sm text-sm text-slate-400">
              {c.newsText}
            </p>
            <form onSubmit={subscribe} className="mt-6 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder="votre@email.fr"
                data-testid="newsletter-email-input"
                className={`${inputCls} flex-1`}
              />
              <button
                type="submit"
                disabled={nlLoading}
                data-testid="newsletter-submit-button"
                aria-label="S'abonner à la newsletter"
                className="clip-frame-sm flex items-center gap-2 bg-neon px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-void transition-colors hover:bg-cyber disabled:opacity-50"
              >
                {nlLoading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Mail size={14} aria-hidden="true" />}
                <span className="hidden sm:inline">S'abonner</span>
              </button>
            </form>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="clip-frame border border-white/10 bg-panel p-8 lg:col-span-7"
        >
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neon">{c.contactEyebrow}</p>
          <h3 className="font-display text-2xl font-bold tracking-tight text-slate-100">
            {c.contactTitle1} <span className="text-neon">{c.contactTitle2}</span>
          </h3>
          <p className="mt-2 text-sm text-slate-400">{c.contactText}</p>

          <form onSubmit={sendContact} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="contact-form">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nom / Pseudonyme"
              data-testid="contact-name-input"
              className={inputCls}
            />
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="votre@email.fr"
              data-testid="contact-email-input"
              className={inputCls}
            />
            <select
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="contact-subject-select"
              aria-label="Sujet de votre message"
              className={`${inputCls} sm:col-span-2`}
            >
              <option>Support technique</option>
              <option>Diagnostic contamination</option>
              <option>Devis volume / professionnel</option>
              <option>Question sur une commande</option>
            </select>
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Décrivez votre protocole, vos paramètres, vos observations…"
              data-testid="contact-message-input"
              className={`${inputCls} resize-none sm:col-span-2`}
            />
            <button
              type="submit"
              disabled={cLoading}
              data-testid="contact-submit-button"
              className="clip-frame-sm flex items-center justify-center gap-2 border border-neon/40 px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neon transition-all hover:bg-neon hover:text-void disabled:opacity-50 sm:col-span-2"
            >
              {cLoading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Transmettre au laboratoire
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default Community;
